import { ParamMap } from '@angular/router';

import { createFeatureSelector, createSelector, Selector } from '@ngrx/store';
import { routerFeatureKey } from './router.reducer';

import { CategoryType } from '@rocksoft/shared';
import { selectAllCategories } from '../category/category.selector';
import { CategoryTypeToEntity } from '../shared/types';
import { Category, ConcreteCategory } from './category';
import { RouterState } from './router.state';

export const selectRouterState = createFeatureSelector<RouterState>(routerFeatureKey);

export const selectParamMap: Selector<object, ParamMap> = createSelector(
  selectRouterState,
  (router) => router.router?.state?.paramMap
);

export const selectUrl: Selector<object, string> = createSelector(
  selectRouterState,
  (router) => router.router?.state?.url
);

export const selectQueryParamMap: Selector<object, ParamMap> = createSelector(
  selectRouterState,
  (router) => router.router?.state?.queryParamsMap
);

export const selectRouterParam = (param: string) =>
  createSelector(selectParamMap, selectQueryParamMap, (paramMap, queryParams) =>
    paramMap?.has(param) ? paramMap.get(param) : queryParams?.has(param) ? queryParams.get(param) : null
  );

export const selectRouteData: Selector<object, Record<string, unknown> | undefined> = createSelector(
  selectRouterState,
  (router) => router.router?.state?.data
);

export const selectRouteDataValue = (key: string) =>
  createSelector(selectRouteData, (data) => (data ? data[key] : undefined));

const ROUTE_PARAM_TO_CATEGORY_TYPE: Record<string, CategoryType> = {
  brandId: 'Brand',
  locationId: 'Location',
  roomId: 'Room',
  barId: 'Bar',
  frontDeskId: 'FrontDesk',
  housekeepingId: 'Housekeeping',
  kioskId: 'Kiosk',
  staffId: 'Staff',
};

const CATEGORY_HIERARCHY_ORDER: CategoryType[] = [
  'Brand',
  'Location',
  'Room',
  'Bar',
  'FrontDesk',
  'Housekeeping',
  'Kiosk',
  'Staff',
];

interface CategoryPair {
  type: CategoryType;
  id: string;
}

/** Extracts category pairs from route params and sorts them by hierarchy order */
function extractCategoryPairsFromParams(paramMap: ParamMap): CategoryPair[] {
  return Object.entries(ROUTE_PARAM_TO_CATEGORY_TYPE)
    .map(([paramName, categoryType]) => {
      const id = paramMap.get(paramName);
      return id ? { type: categoryType, id } : null;
    })
    .filter((pair): pair is CategoryPair => pair !== null)
    .sort((a, b) => CATEGORY_HIERARCHY_ORDER.indexOf(a.type) - CATEGORY_HIERARCHY_ORDER.indexOf(b.type));
}

/** Checks if all required non-Brand category types have been loaded */
function areCategoriesLoaded(categoryPairs: CategoryPair[], categories: { categoryType: CategoryType }[]): boolean {
  return categoryPairs
    .filter(({ type }) => type !== 'Brand')
    .every(({ type }) => categories.some((cat) => cat.categoryType === type));
}

/** Creates the default brand category */
function createDefaultBrandCategory(): ConcreteCategory<'Brand'> {
  return new ConcreteCategory('Brand', 'default-brand', 'default-brand', 'Default Brand', undefined, undefined);
}

/** Builds a category chain from category pairs, linking each to its parent */
function buildCategoryChain(
  categoryPairs: CategoryPair[],
  categories: {
    categoryType: CategoryType;
    categoryId: string;
    categoryHumanId: string;
    categoryDisplayName: string;
    entity: CategoryTypeToEntity[CategoryType] | undefined;
  }[]
): Category | undefined {
  return categoryPairs.reduce<Category | undefined>((parent, { type, id }) => {
    if (type === 'Brand' && id === 'default-brand') {
      return createDefaultBrandCategory();
    }

    const categoryEntity = categories.find((cat) => cat.categoryType === type && cat.categoryHumanId === id);
    if (!categoryEntity) {
      return parent;
    }

    return new ConcreteCategory(
      categoryEntity.categoryType,
      categoryEntity.categoryId,
      categoryEntity.categoryHumanId,
      categoryEntity.categoryDisplayName,
      categoryEntity.entity,
      parent
    );
  }, undefined);
}

export const selectCurrentCategoryFromRouter = createSelector(
  selectParamMap,
  selectAllCategories,
  (paramMap, categories) => {
    if (!paramMap || !categories) {
      return undefined;
    }

    const categoryPairs = extractCategoryPairsFromParams(paramMap);
    if (categoryPairs.length === 0) {
      return undefined;
    }

    if (!areCategoriesLoaded(categoryPairs, categories)) {
      return undefined;
    }

    return buildCategoryChain(categoryPairs, categories);
  }
);
